// src/pages/Lounge/MyNotifications/MyNotifications.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { motion as Motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import api from "@/utils/axios";
import i18n from "@/i18n";
import { useNotifications } from "@/app/notification-context-state";

import cls from "./MyNotifications.module.scss";

const PAGE_SIZE = 10;

const fadeIn = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

const listStagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

function formatRelativeTime(dateInput) {
  if (!dateInput) return "";
  const created =
    typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (Number.isNaN(created.getTime())) return "";

  const now = new Date();
  const diffMs = now - created;
  if (diffMs < 0) return "";

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  }

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
  }

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) {
    return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  }

  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) {
    return `${diffMonth} month${diffMonth > 1 ? "s" : ""} ago`;
  }

  const diffYear = Math.floor(diffMonth / 12);
  return `${diffYear} year${diffYear > 1 ? "s" : ""} ago`;
}

const MyNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const notificationsCtx = useNotifications();

  const effectiveUnreadCount =
    typeof notificationsCtx?.unreadCount === "number"
      ? notificationsCtx.unreadCount
      : unreadCount;

  const fetchPage = useCallback(
    async (pageToLoad, append = false) => {
      try {
        if (pageToLoad === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
        setError("");

        const res = await api.get("/v1/member/notifications", {
          params: { page: pageToLoad, limit: PAGE_SIZE },
        });

        const data = res.data || {};
        const newItems = data.notifications || [];

        setNotifications((prev) =>
          append ? [...prev, ...newItems] : newItems,
        );
        setHasMore(!!data.hasMore);

        const nextUnread =
          typeof data.unreadCount === "number" ? data.unreadCount : 0;
        setUnreadCount(nextUnread);
        if (
          notificationsCtx &&
          typeof notificationsCtx.syncUnreadCount === "function"
        ) {
          notificationsCtx.syncUnreadCount(nextUnread);
        }

        setPage(pageToLoad);
      } catch (err) {
        console.error(err);
        setError(
          err?.response?.data?.message ||
            i18n.t("memberNotification.error.generic"),
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [notificationsCtx],
  );

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  const handleSeeMore = () => {
    if (!hasMore || loadingMore) return;
    fetchPage(page + 1, true);
  };

  const handleMarkRead = async (id) => {
    try {
      if (
        notificationsCtx &&
        typeof notificationsCtx.markAsRead === "function"
      ) {
        await notificationsCtx.markAsRead(id);
      } else {
        await api.patch(`/v1/member/notifications/${id}/read`);
      }

      setNotifications((prev) =>
        prev.map((n) =>
          n._id === id || n.id === id
            ? {
                ...n,
                isRead: true,
                readAt: n.readAt || new Date().toISOString(),
              }
            : n,
        ),
      );

      setUnreadCount((prev) => Math.max(prev - 1, 0));

      if (
        !notificationsCtx ||
        typeof notificationsCtx.markAsRead !== "function"
      ) {
        if (
          notificationsCtx &&
          typeof notificationsCtx.syncUnreadCount === "function"
        ) {
          const current =
            typeof notificationsCtx.unreadCount === "number"
              ? notificationsCtx.unreadCount
              : 0;
          notificationsCtx.syncUnreadCount(Math.max(current - 1, 0));
        }
      }
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          i18n.t("memberNotification.error.markRead"),
      );
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setMarkingAll(true);
      setError("");

      if (
        notificationsCtx &&
        typeof notificationsCtx.markAllAsRead === "function"
      ) {
        await notificationsCtx.markAllAsRead();
      } else {
        await api.patch("/v1/member/notifications/mark-all-read");
      }

      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          isRead: true,
          readAt: n.readAt || new Date().toISOString(),
        })),
      );
      setUnreadCount(0);

      if (
        notificationsCtx &&
        typeof notificationsCtx.syncUnreadCount === "function"
      ) {
        notificationsCtx.syncUnreadCount(0);
      }
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          i18n.t("memberNotification.error.markAllRead"),
      );
    } finally {
      setMarkingAll(false);
    }
  };

  const handleBack = () => {
    navigate("/lounge");
  };

  const sortedNotifications = useMemo(() => {
    const copy = [...notifications];
    copy.sort((a, b) => {
      const aRead = !!a.isRead;
      const bRead = !!b.isRead;

      if (aRead !== bRead) {
        return aRead - bRead; // unread first
      }

      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime; // newest first
    });
    return copy;
  }, [notifications]);

  const renderCard = (notif) => {
    const id = notif._id || notif.id;
    const type = notif.type || "default";
    const isRead = !!notif.isRead;

    const createdAt = notif.createdAt ? new Date(notif.createdAt) : null;
    const relativeTime = createdAt ? formatRelativeTime(createdAt) : "";

    const isPaymentApproved = type === "payment_approved";
    const isPaymentDecline = type === "payment_decline";

    let cardClass = cls.cardDefault;
    if (isPaymentApproved) cardClass = cls.cardPaymentApproved;
    else if (isPaymentDecline) cardClass = cls.cardPaymentDecline;

    const baseTitle = notif.title || "";
    const baseDescription = notif.description || "";

    const displayTitle = isPaymentApproved
      ? i18n.t("memberNotification.types.payment_approved.title")
      : isPaymentDecline
        ? i18n.t("memberNotification.types.payment_decline.title")
        : baseTitle;

    const displayDescription = isPaymentApproved
      ? i18n.t("memberNotification.types.payment_approved.description")
      : isPaymentDecline
        ? i18n.t("memberNotification.types.payment_decline.description")
        : baseDescription;

    return (
      <Motion.article
        key={id}
        className={`${cls.card} ${cardClass} ${isRead ? cls.cardRead : ""}`}
        variants={fadeIn}
      >
        <div className={cls.cardGlow} aria-hidden="true" />
        <div className={cls.cardSideAccent} aria-hidden="true" />

        <div className={cls.cardInner}>
          <div className={cls.cardTopRow}>
            <div className={cls.iconBubble} aria-hidden="true">
              <span className={cls.iconLetter}>
                {displayTitle && displayTitle.charAt(0)
                  ? displayTitle.charAt(0)
                  : "N"}
              </span>
            </div>

            <div className={cls.cardHeader}>
              <h3 className={cls.cardTitle}>{displayTitle}</h3>
              {displayDescription && (
                <p className={cls.cardDescription}>{displayDescription}</p>
              )}
            </div>
          </div>

          <div className={cls.cardBottomRow}>
            <div className={cls.cardMeta}>
              {relativeTime && (
                <span className={cls.cardTime}>{relativeTime}</span>
              )}
              <span
                className={`${cls.readStatus} ${
                  isRead ? cls.readStatusRead : cls.readStatusUnread
                }`}
              >
                {isRead
                  ? i18n.t("memberNotification.status.read")
                  : i18n.t("memberNotification.status.unread")}
              </span>
            </div>

            <div className={cls.cardActions}>
              {!isRead && (
                <button
                  type="button"
                  className={cls.markReadBtn}
                  onClick={() => handleMarkRead(id)}
                >
                  {i18n.t("memberNotification.actions.markRead")}
                </button>
              )}
              {notif.ctaPath ? (
                <Link
                  to={notif.ctaPath}
                  className={cls.ctaLink}
                  onClick={() => {
                    if (!isRead) {
                      handleMarkRead(id);
                    }
                  }}
                >
                  {notif.ctaLabel || notif.ctaPath}
                </Link>
              ) : notif.ctaLabel ? (
                <span className={cls.ctaLabelStatic}>{notif.ctaLabel}</span>
              ) : null}
            </div>
          </div>
        </div>
      </Motion.article>
    );
  };

  const hasNotifications = notifications.length > 0;

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.headerMain}>
          <h1 className={cls.title}>{i18n.t("memberNotification.title")}</h1>
          <p className={cls.subtitle}>
            {i18n.t("memberNotification.subtitle")}
          </p>
        </div>

        <div className={cls.headerActions}>
          <span className={cls.unreadPill}>
            {i18n.t("memberNotification.unreadCount", {
              count: effectiveUnreadCount,
            })}
          </span>

          <button
            type="button"
            className={cls.markAllBtn}
            disabled={markingAll || effectiveUnreadCount === 0}
            onClick={handleMarkAllRead}
          >
            {markingAll
              ? i18n.t("memberNotification.actions.markAllReadWorking")
              : i18n.t("memberNotification.actions.markAllRead")}
          </button>
        </div>
      </header>

      <div className={cls.backRow}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          <span className={cls.backIcon} aria-hidden="true">
            ←
          </span>
          <span>{i18n.t("memberMyProfile.backToLounge")}</span>
        </button>
      </div>

      {error && (
        <div className={cls.errorBox}>
          <span className={cls.errorIcon} aria-hidden="true">
            ⚠️
          </span>
          <span className={cls.errorText}>{error}</span>
        </div>
      )}

      {loading && !hasNotifications && (
        <div className={cls.skeletonList}>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className={cls.skeletonCard} />
          ))}
        </div>
      )}

      {!loading && !hasNotifications && !error && (
        <div className={cls.emptyState}>
          <div className={cls.emptyGlow} aria-hidden="true" />
          <p className={cls.emptyTitle}>
            {i18n.t("memberNotification.empty.title")}
          </p>
          <p className={cls.emptyBody}>
            {i18n.t("memberNotification.empty.body")}
          </p>
        </div>
      )}

      {hasNotifications && (
        <div className={cls.listWrapper}>
          <Motion.section
            className={cls.list}
            variants={listStagger}
            initial="hidden"
            animate="show"
          >
            {sortedNotifications.map(renderCard)}
          </Motion.section>

          <div className={cls.footerRow}>
            {hasMore && (
              <button
                type="button"
                className={cls.seeMoreBtn}
                onClick={handleSeeMore}
                disabled={loadingMore}
              >
                {loadingMore
                  ? i18n.t("memberNotification.actions.loadingMore")
                  : i18n.t("memberNotification.actions.seeMore")}
              </button>
            )}
            {!hasMore && hasNotifications && (
              <span className={cls.noMoreText}>
                {i18n.t("memberNotification.noMore")}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyNotifications;
